<template>
  <div>
    <Sidenav
      :is-sidebar-active.sync="isSidebarActive"
      :item-edit.sync="itemEdit"
      :is-add.sync="isAdd"
      :maintenance-company-options="maintenanceCompanyOptions"
      @refetch-data="refetchData"
    />

    <b-row class="pt-2 pb-1">
      <b-col cols="6">
        <BreadcrumbMenu/>
      </b-col>
      <b-col cols="6" class="pr-3 text-right">
        <button-filters
          @show-modal="$root.$emit('bv::show::modal', $event)"
          :filters-count="filtersCount"
          @clear-search-filters="clearSearchFilters"
        />
      </b-col>
    </b-row>

    <!-- Filters -->
    <filter-modal @action="buildFilters()" @clear="clearSearchFilters()">
      <template v-slot:main-filters>
        <filters
          :maintenance-company-options="maintenanceCompanyOptions"
          :maintenance-company-filter.sync="maintenanceCompanyFilter"
        />
      </template>
    </filter-modal>

    <toastification-loading-content ref="loadingToast" />

    <!-- Table Container Card -->
    <b-card class="m-2" no-body>
      <div class="m-2">
        <b-row>
          <b-col
            cols="12"
            md="6"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >
            <label class="text-capitalize">{{ $t("table.show") }}</label>
            <v-select
              v-model="perPage"
              :options="perPageOptions"
              :clearable="false"
              class="per-page-selector mx-50"
            />
            <label class="text-capitalize">{{ $t("table.entries") }}</label>
          </b-col>

          <b-col cols="12" md="6">
            <div class="d-flex align-items-center justify-content-end">
              <b-form-input
                v-model="searchQuery"
                class="d-inline-block mr-1"
                :placeholder="$t('table.search') + '...'"
              />
              <b-button
                variant="secondary"
                @click="openAdd()"
                v-if="$can('create', 'MaintenanceCrew')"
              >
                <span>{{
                  $t("actions.add") + " " + $t("maintenance.maintenance_crew")
                }}</span>
              </b-button>
            </div>
          </b-col>
        </b-row>
      </div>

      <b-table
        ref="refListTable"
        :items="fetchList"
        responsive
        :fields="tableColumns"
        primary-key="id"
        :sort-by.sync="sortBy"
        show-empty
        :sort-desc.sync="isSortDirDesc"
      >
        <template #head()="data">
          <span> {{ $tc(data.label, 2) }}</span>
        </template>

        <template #head(actions)="data">
          <span> {{ $tc(data.label, 2) }}</span>
        </template>

        <template #cell(created_at)="data">
          <span>{{ formatDateAssigned(data.item.created_at) }}</span>
        </template>

        <template #cell(updated_at)="data">
          <span>{{ formatDateAssigned(data.item.updated_at) }}</span>
        </template>

        <template #cell(description)="data">
          {{ data.item.description }}
        </template>

        <template #cell(leader)="data">
          {{ formatLeader(data.item.leader) }}
        </template>

        <template #cell(users)="data">
          {{ formatUsers(data.item.users) }}
        </template>

        <template #cell(actions)="data">
          <b-dropdown variant="link" no-caret :right="$store.state.appConfig.isRTL">
            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>

            <b-dropdown-item
              @click="openEdit(data.item)"
              v-if="$can('update', 'MaintenanceCrew')"
            >
              <feather-icon icon="EditIcon" />
              <span>{{ $t("actions.edit") }}</span>
            </b-dropdown-item>

            <b-dropdown-item
              @click="confirmDelete(data.item.id)"
              v-if="$can('delete', 'MaintenanceCrew')"
            >
              <feather-icon icon="TrashIcon" />
              <span>{{ $t("actions.delete") }}</span>
            </b-dropdown-item>
          </b-dropdown>
        </template>
      </b-table>
      <div class="mx-2 mb-2">
        <b-row>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-start"
          >
            <span class="text-muted text-capitalize">{{
              $t("table.showing", {
                from: dataMeta.from,
                to: dataMeta.to,
                of: dataMeta.of,
              })
            }}</span>
          </b-col>
          <!-- Pagination -->
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-end"
          >
            <Pagination
              :totalList="totalList"
              :currentPage="currentPage"
              @updatePagination="update"
              :perPage="perPage"
            >
            </Pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>
  </div>
</template>

<script>
import { ref, getCurrentInstance } from "@vue/composition-api";
import useList from "./useList";
import Sidenav from "./Sidenav.vue";
import Filters from "./Filters.vue";
import Pagination from "@/components/Pagination.vue";
import BreadcrumbMenu from "@/components/BreadcrumbMenu.vue";
import { useToast } from "vue-toastification/composition";
import ToastificationLoadingContent from "@core/components/toastification/ToastificationLoadingContent.vue";
import vSelect from "vue-select";
import store from "@/store";
import FilterModal from "@/components/Filters/FilterModal.vue";
import ButtonFilters from "@/components/Filters/ButtonFilters.vue";

import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BTable,
  BDropdown,
  BDropdownItem,
} from "bootstrap-vue";

export default {
  components: {
    BreadcrumbMenu,
    Sidenav,
    Filters,
    Pagination,
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BDropdown,
    BDropdownItem,
    vSelect,
    ToastificationLoadingContent,
    FilterModal,
    ButtonFilters,
  },
  setup() {
    const nameNav = ref("breadcrumbs.maintenance.maintenance.crews");
    const isSidebarActive = ref(false);
    const itemEdit = ref({});
    const isAdd = ref(false);
    const toast = useToast();
    const loadingToast = ref();

    const {
      maintenanceCompanyOptions, // 👈 opciones para el select de company
      maintenanceCompanyFilter, // (por si luego lo ocupas en filtros)
      refListTable,
      tableColumns,
      perPage,
      totalList,
      currentPage,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      fetchList,
      refetchData,
      dataMeta,
      formatDateAssigned,
      filtersCount,
      clearSearchFilters,
      buildFilters,
    } = useList();

    const openEdit = (item) => {
      itemEdit.value = { ...item };
      isSidebarActive.value = true;
      isAdd.value = false;
    };

    const openAdd = () => {
      isAdd.value = true;
      isSidebarActive.value = true;
    };

    const formatLeader = (leader) => {
      if (!leader) return "";
      const name = leader.name || "";
      const firstName = leader.first_name || "";
      const parts = [name, firstName].filter(part => part && part.trim());
      return parts.join(" ").trim();
    };

    const formatUsers = (users) => {
      if (!Array.isArray(users)) return "";
      return users
        .map((user) => {
          const name = user.name || "";
          const firstName = user.first_name || "";
          const secondName = user.second_name || "";
          const parts = [name, firstName, secondName].filter(part => part && part.trim());
          return parts.join(" ").trim();
        })
        .filter(name => name) // Filtrar nombres vacíos
        .join(", ");
    };

    const { proxy } = getCurrentInstance();

    const confirmDelete = (id) => {
      proxy
        .$swal({
          title: "Confirm Delete",
          text: "Are you sure you want to delete this maintenance crew?",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Delete",
        })
        .then((result) => {
          if (result.value) {
            store.dispatch("maintenance_crew/delete", id).then((response) => {
              if (response.success) {
                toast.success(response.message);
                refetchData();
              } else {
                toast.error(response.message);
              }
            });
          }
        });
    };

    const itemFilter = ref();
    const addItemFilter = (data) => {
      itemFilter.value = data;
    };
    const addItemFilterSearch = (data) => {
      data.forEach((element) => {
        if (nameNav.value === element.text) {
          searchQuery.value = element.searchQuery;
        }
      });
    };

    const update = (page) => {
      currentPage.value = page;
    };

    return {
      nameNav,
      isSidebarActive,
      itemEdit,
      isAdd,
      loadingToast,
      openEdit,
      openAdd,
      formatLeader,
      formatUsers,
      refListTable,
      tableColumns,
      perPage,
      totalList,
      currentPage,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      fetchList,
      refetchData,
      confirmDelete,
      itemFilter,
      addItemFilter,
      addItemFilterSearch,
      update,
      maintenanceCompanyOptions,
      maintenanceCompanyFilter,
      dataMeta,
      formatDateAssigned,
      filtersCount,
      clearSearchFilters,
      buildFilters,
    };
  },
};
</script>
